import { createFileRoute } from '@tanstack/react-router'
import { useRef, useState } from 'react'
import { Check, Copy, Image as ImageIcon, RefreshCw, Trash2, UploadCloud } from 'lucide-react'
import { useImages, useUploadImage, useDeleteImage } from '@/hooks/data/useImages'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'
import { useAlertContext } from '@/providers/AlertProvider'
import { resolveMediaUrl } from '@/utils/mediaUrl';

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function ImagesPage() {
  const [category, setCategory] = useState('')
  const { data: images = [], isLoading, isError, error, isFetching, refetch } = useImages(category || undefined)
  const upload = useUploadImage()
  const del = useDeleteImage()
  const confirmDelete = useDeleteConfirm()
  const { error: showError } = useAlertContext()
  const [isDragging, setIsDragging] = useState(false)
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFiles = async (files: FileList | File[]) => {
    for (const file of Array.from(files)) {
      try {
        await upload.mutateAsync({ file, category: category || undefined })
      } catch (err) {
        showError('Upload failed', `${file.name}: ${(err as Error)?.message || 'Failed to upload'}`)
      }
    }
  }

  const copyUrl = async (url: string) => {
    await navigator.clipboard.writeText(url)
    setCopiedUrl(url)
    setTimeout(() => setCopiedUrl(null), 1500)
  }

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <ImageIcon className="w-6 h-6" /> Image Library
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Upload and manage images (ImagesController).</p>
        </div>
        <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <input
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        placeholder="Filter by category…"
        className="w-full md:w-72 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
      />

      <div
        onDragOver={(e) => {
          e.preventDefault()
          setIsDragging(true)
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setIsDragging(false)
          if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files)
        }}
        onClick={() => fileInputRef.current?.click()}
        className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
          isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
        }`}
      >
        <UploadCloud className="w-8 h-8 text-gray-400" />
        <p className="text-sm text-gray-500">Drag and drop images here, or click to browse</p>
        {upload.isPending && <p className="text-xs text-blue-600">Uploading…</p>}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) handleFiles(e.target.files)
            e.target.value = ''
          }}
        />
      </div>

      {isLoading ? (
        <p className="p-6 text-gray-500">Loading images…</p>
      ) : isError ? (
        <p className="p-6 text-red-600">Failed to load images: {(error as Error)?.message}.</p>
      ) : images.length === 0 ? (
        <p className="p-6 text-gray-500">No images found.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {images.map((img) => (
            <div key={img.fileName} className="group relative rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <img src={resolveMediaUrl(img.url)} alt={img.fileName} className="w-full aspect-square object-cover" />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button onClick={() => copyUrl(img.url)} className="p-2 rounded-lg bg-white/90 hover:bg-white" title="Copy URL">
                  {copiedUrl === img.url ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-700" />}
                </button>
                <button
                  onClick={() =>
                    confirmDelete(img.fileName, async () => {
                      await del.mutateAsync(img.fileName)
                    })
                  }
                  className="p-2 rounded-lg bg-white/90 hover:bg-white"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              </div>
              <div className="p-1.5 text-[10px] text-gray-500 truncate">
                {formatBytes(img.bytes)} · {img.width}×{img.height}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export const Route = createFileRoute('/(admin)/image-library/')({
  component: ImagesPage,
})
